import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface ParentConnection {
  parentId: string;
  parentUsername: string;
  connectedAt: string;
}

interface UseChildParentConnectionStatusResult {
  hasParentConnected: boolean;
  parents: ParentConnection[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export const useChildParentConnectionStatus = (childId: string | undefined): UseChildParentConnectionStatusResult => {
  const [hasParentConnected, setHasParentConnected] = useState(false);
  const [parents, setParents] = useState<ParentConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConnectionStatus = useCallback(async () => {
    if (!childId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Query parent_child_connections for this child
      const { data: connections, error: connError } = await supabase
        .from('parent_child_connections')
        .select('parent_id, connected_at')
        .eq('child_id', childId)
        .eq('is_active', true);

      if (connError) {
        console.error('Error fetching parent connections:', connError);
        setError(connError.message);
        setLoading(false);
        return;
      }

      if (!connections || connections.length === 0) {
        setHasParentConnected(false);
        setParents([]);
        setLoading(false);
        return;
      }

      // Fetch parent usernames
      const parentIds = connections.map(c => c.parent_id);
      const { data: parentUsers, error: usersError } = await supabase
        .from('internal_users')
        .select('id, username')
        .in('id', parentIds);

      if (usersError) {
        console.error('Error fetching parent users:', usersError);
      }

      const parentMap = new Map(parentUsers?.map(u => [u.id, u.username]) || []);

      const enrichedParents: ParentConnection[] = connections.map(conn => ({
        parentId: conn.parent_id,
        parentUsername: parentMap.get(conn.parent_id) || 'Unknown',
        connectedAt: conn.connected_at
      }));

      setHasParentConnected(true);
      setParents(enrichedParents);
    } catch (err) {
      console.error('Error in useChildParentConnectionStatus:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    fetchConnectionStatus();
  }, [fetchConnectionStatus]);

  return {
    hasParentConnected,
    parents,
    loading,
    error,
    refresh: fetchConnectionStatus
  };
};
